// ./src/utils/api/VehicleTaskService.ts
import { gql } from "@apollo/client";
import { ApiService } from "./ApiService";
import { OpResult } from "@sdflc/api-helpers";
import { Pagination, SortItem } from "./commonApiTypes";
import { User } from "./UserService";
import { Car } from "./CarService";
import { Expense } from "./ExpenseService";

// =============================================================================
// GraphQL Fragments
// =============================================================================

const VEHICLE_TASK_FIELDS = `
  id
  accountId
  orderNo
  carId
  assignedToUserId
  priority
  title
  notes
  category
  dueDate
  reminderDate
  scheduleType
  scheduleDays
  completedAt
  completedByUserId
  linkedExpenseId
  isOverdue
  daysUntilDue
  urgencyStatus
  status
  version
  createdBy
  updatedBy
  createdAt
  updatedAt
  car {
    id
    label
  }
  assignedToUser {
    id
    firstName
    lastName
    email
  }
  completedByUser {
    id
    firstName
    lastName
  }
  linkedExpense {
    id
    totalPrice
    paidInCurrency
  }
  userCreated {
    id
    firstName
    lastName
  }
  userUpdated {
    id
    firstName
    lastName
  }
`;

// =============================================================================
// GraphQL Queries
// =============================================================================

const VEHICLE_TASK_LIST_QUERY = gql`
  query VehicleTaskList($filter: VehicleTaskFilter, $params: PaginationAndSorting) {
    vehicleTaskList(filter: $filter, params: $params) {
      code
      data {
        ${VEHICLE_TASK_FIELDS}
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const VEHICLE_TASK_GET_QUERY = gql`
  query VehicleTaskGet($id: ID) {
    vehicleTaskGet(id: $id) {
      code
      data {
        ${VEHICLE_TASK_FIELDS}
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const VEHICLE_TASK_GET_MANY_QUERY = gql`
  query VehicleTaskGetMany($ids: [ID]) {
    vehicleTaskGetMany(ids: $ids) {
      code
      data {
        ${VEHICLE_TASK_FIELDS}
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

// =============================================================================
// GraphQL Mutations
// =============================================================================

const VEHICLE_TASK_CREATE_MUTATION = gql`
  mutation VehicleTaskCreate($params: VehicleTaskInput) {
    vehicleTaskCreate(params: $params) {
      code
      data {
        ${VEHICLE_TASK_FIELDS}
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const VEHICLE_TASK_UPDATE_MUTATION = gql`
  mutation VehicleTaskUpdate($where: VehicleTaskWhereInput, $params: VehicleTaskInput) {
    vehicleTaskUpdate(where: $where, params: $params) {
      code
      data {
        ${VEHICLE_TASK_FIELDS}
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const VEHICLE_TASK_COMPLETE_MUTATION = gql`
  mutation VehicleTaskComplete($where: VehicleTaskWhereInput) {
    vehicleTaskComplete(where: $where) {
      code
      data {
        ${VEHICLE_TASK_FIELDS}
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const VEHICLE_TASK_REMOVE_MUTATION = gql`
  mutation VehicleTaskRemove($where: VehicleTaskWhereInput) {
    vehicleTaskRemove(where: $where) {
      code
      data {
        id
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const VEHICLE_TASK_REMOVE_MANY_MUTATION = gql`
  mutation VehicleTaskRemoveMany($where: [VehicleTaskWhereInput]) {
    vehicleTaskRemoveMany(where: $where) {
      code
      data {
        id
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const VEHICLE_TASK_DASHBOARD_QUERY = gql`
  query VehicleTaskDashboard($params: VehicleTaskDashboardInput) {
    vehicleTaskDashboard(params: $params) {
      code
      data {
        ${VEHICLE_TASK_FIELDS}
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

// =============================================================================
// Types
// =============================================================================

/**
 * Task status constants
 */
export const VEHICLE_TASK_STATUS = {
  IN_PROGRESS: 50, // Make it 50 as we list by status first and then by order no
  TODO: 100,
  COMPLETE: 300,
  REMOVED: 10000,
} as const;

export type VehicleTaskStatusValue = (typeof VEHICLE_TASK_STATUS)[keyof typeof VEHICLE_TASK_STATUS];

/**
 * Task priority constants
 */
export const VEHICLE_TASK_PRIORITY = {
  LOW: 100,
  MEDIUM: 200,
  HIGH: 300,
} as const;

export type VehicleTaskPriorityValue = (typeof VEHICLE_TASK_PRIORITY)[keyof typeof VEHICLE_TASK_PRIORITY];

/**
 * Re-export schedule type from ExpenseScheduleService for consistency.
 * Tasks use the same recurrence pattern: weekly, monthly, yearly, one-time.
 */
export { SCHEDULE_TYPE } from "./ExpenseScheduleService";
export type { ScheduleTypeValue } from "./ExpenseScheduleService";

export interface VehicleTask {
  id?: string | undefined;
  accountId?: string | undefined;
  orderNo?: number | undefined;

  /** Reference to the vehicle (null = account-wide task) */
  carId?: string | undefined;

  /** User who should complete the task (null = unassigned) */
  assignedToUserId?: string | undefined;

  /**
   * Task priority:
   * - 100: Low (no urgency)
   * - 200: Medium (should be done soon)
   * - 300: High (needs immediate attention)
   */
  priority?: VehicleTaskPriorityValue | undefined;

  // Task details
  /** Short task description */
  title?: string | undefined;
  /** Additional details or instructions */
  notes?: string | undefined;
  /** Task type for grouping: administrative, purchase, appointment, seasonal, repair */
  category?: string | undefined;

  // Dates
  /** Optional due date/time in UTC */
  dueDate?: string | undefined;
  /** When to send a reminder notification (in UTC) */
  reminderDate?: string | undefined;

  // Recurrence (same pattern as expense schedules)
  /**
   * Recurrence type:
   * - weekly: repeats on specified days of week (1=Mon, 7=Sun)
   * - monthly: repeats on specified days of month (1-31, 'last')
   * - yearly: repeats on specified dates (MM-DD format)
   * - one-time: single occurrence on specified date
   * - null: no recurrence (ad-hoc task)
   */
  scheduleType?: string | undefined;
  /** Days/dates for recurrence (format depends on scheduleType) */
  scheduleDays?: string | undefined;

  // Completion
  /** When the task was marked complete (in UTC) */
  completedAt?: string | undefined;
  /** User who completed the task */
  completedByUserId?: string | undefined;
  /** Reference to expense created via 'convert to expense' workflow */
  linkedExpenseId?: string | undefined;

  isOverdue?: boolean | undefined;
  daysUntilDue?: number | undefined;
  urgencyStatus?: string | undefined;

  // Status and audit fields
  /**
   * Task status:
   * - 100: Todo (pending, not yet started)
   * - 200: In Progress (someone is working on it)
   * - 300: Complete (done)
   * - 10000: Removed (soft-deleted)
   */
  status?: VehicleTaskStatusValue | undefined;
  version?: number | undefined;
  createdBy?: string | undefined;
  updatedBy?: string | undefined;
  createdAt?: string | undefined;
  updatedAt?: string | undefined;

  // Resolved references
  car?: Car | undefined;
  /** User assigned to complete this task */
  assignedToUser?: User | undefined;
  /** User who completed this task */
  completedByUser?: User | undefined;
  /** Linked expense record (if task was converted to expense) */
  linkedExpense?: Expense | undefined;
  userCreated?: User | undefined;
  userUpdated?: User | undefined;
}

export interface VehicleTaskInput {
  id?: string | null | undefined;
  orderNo?: number | null | undefined;

  /** Vehicle this task belongs to (null = account-wide task) */
  carId?: string | null | undefined;
  /** User who should complete the task */
  assignedToUserId?: string | null | undefined;
  /** Priority: 100=Low, 200=Medium, 300=High */
  priority?: VehicleTaskPriorityValue | null | undefined;

  // Task details
  /** Short task description */
  title?: string | null | undefined;
  /** Additional details or instructions */
  notes?: string | null | undefined;
  /** Task type for grouping */
  category?: string | null | undefined;

  // Dates
  /** Due date/time in UTC */
  dueDate?: string | null | undefined;
  /** When to send a reminder notification (in UTC) */
  reminderDate?: string | null | undefined;

  // Recurrence
  /** Recurrence type: weekly, monthly, yearly, one-time */
  scheduleType?: string | null | undefined;
  /** Recurrence days/dates (format depends on scheduleType) */
  scheduleDays?: string | null | undefined;

  // Completion
  /** When the task was completed (in UTC, set automatically on status change) */
  completedAt?: string | null | undefined;
  /** User who completed the task (set automatically on status change) */
  completedByUserId?: string | null | undefined;
  /** Link to an expense record */
  linkedExpenseId?: string | null | undefined;

  /** Status: 100=Todo, 200=In Progress, 300=Complete */
  status?: VehicleTaskStatusValue | null | undefined;
}

export interface VehicleTaskFilter {
  id?: string[] | null | undefined;
  accountId?: string[] | null | undefined;
  carId?: string[] | null | undefined;
  assignedToUserId?: string[] | null | undefined;
  createdByUserId?: string[] | null | undefined;
  completedByUserId?: string[] | null | undefined;
  /** Filter by priority: 100=Low, 200=Medium, 300=High */
  priority?: VehicleTaskPriorityValue[] | null | undefined;
  /** Filter by category */
  category?: string[] | null | undefined;
  /** Filter by schedule type: weekly, monthly, yearly, one-time */
  scheduleType?: string[] | null | undefined;
  /** Filter by status: 100=Todo, 200=In Progress, 300=Complete */
  status?: VehicleTaskStatusValue[] | null | undefined;
  /** Filter tasks with due date on or after this date */
  dueDateFrom?: string | null | undefined;
  /** Filter tasks with due date on or before this date */
  dueDateTo?: string | null | undefined;
  /** Filter tasks with reminder date on or after this date */
  reminderDateFrom?: string | null | undefined;
  /** Filter tasks with reminder date on or before this date */
  reminderDateTo?: string | null | undefined;
  /** Filter by linked expense */
  linkedExpenseId?: string[] | null | undefined;
  /** Search in title, notes, category */
  searchKeyword?: string | null | undefined;
}

export interface VehicleTaskDashboardInput {
  /** Filter by specific vehicle(s). Null = all accessible vehicles. */
  carId?: string[] | null | undefined;
  /** Number of days ahead to consider 'due soon'. Falls back to UserProfile.notifyInDays if not provided. */
  dueSoonDays?: number | null | undefined;
  /** Maximum number of tasks to return (default: 20) */
  limit?: number | null | undefined;
}

export interface VehicleTaskWhereInput {
  id?: string | undefined;
}

// =============================================================================
// Helper Functions
// =============================================================================

/**
 * Check if a task is overdue (has a due date in the past and is not complete)
 */
export function isTaskOverdue(task: VehicleTask): boolean {
  if (!task.dueDate || task.status === VEHICLE_TASK_STATUS.COMPLETE || task.status === VEHICLE_TASK_STATUS.REMOVED) {
    return false;
  }
  return new Date(task.dueDate) < new Date();
}

/**
 * Check if a task is due within the next N days
 */
export function isTaskDueSoon(task: VehicleTask, days: number = 7): boolean {
  if (!task.dueDate || task.status === VEHICLE_TASK_STATUS.COMPLETE || task.status === VEHICLE_TASK_STATUS.REMOVED) {
    return false;
  }
  const dueDate = new Date(task.dueDate);
  const now = new Date();
  const threshold = new Date();
  threshold.setDate(now.getDate() + days);
  return dueDate >= now && dueDate <= threshold;
}

/**
 * Check if a task has recurrence configured
 */
export function isRecurringTask(task: VehicleTask): boolean {
  return !!task.scheduleType && task.scheduleType !== 'one-time';
}

// =============================================================================
// Service Class
// =============================================================================

/**
 * Vehicle task service that handles all task-related API calls.
 * Supports CRUD operations plus a dedicated complete action.
 */
export class VehicleTaskService {
  /**
   * Get a list of vehicle tasks with optional filtering, pagination, and sorting
   */
  static async list(
    filter?: VehicleTaskFilter,
    pagination?: Pagination,
    sortBy?: SortItem[]
  ): Promise<OpResult> {
    const result = await ApiService.executeQuery(VEHICLE_TASK_LIST_QUERY, {
      filter: filter || {},
      params: {
        pagination: pagination || undefined,
        sortBy: sortBy || undefined,
      },
    });
    return result;
  }

  /**
   * Get a single vehicle task by ID
   */
  static async get(id: string): Promise<OpResult> {
    const result = await ApiService.executeQuery(VEHICLE_TASK_GET_QUERY, {
      id,
    });
    return result;
  }

  /**
   * Get multiple vehicle tasks by IDs
   */
  static async getMany(ids: string[]): Promise<OpResult> {
    const result = await ApiService.executeQuery(VEHICLE_TASK_GET_MANY_QUERY, {
      ids,
    });
    return result;
  }

  /**
   * Get tasks needing attention for the dashboard widget.
   * Returns overdue, due soon, and in-progress tasks.
   */
  static async dashboard(params?: VehicleTaskDashboardInput): Promise<OpResult> {
    const result = await ApiService.executeQuery(VEHICLE_TASK_DASHBOARD_QUERY, {
      params: params || {},
    });
    return result;
  }

  /**
   * Create a new vehicle task
   */
  static async create(params: VehicleTaskInput): Promise<OpResult> {
    const result = await ApiService.executeMutation(VEHICLE_TASK_CREATE_MUTATION, {
      params,
    });
    return result;
  }

  /**
   * Update an existing vehicle task
   */
  static async update(
    where: VehicleTaskWhereInput,
    params: VehicleTaskInput
  ): Promise<OpResult> {
    const result = await ApiService.executeMutation(VEHICLE_TASK_UPDATE_MUTATION, {
      where,
      params,
    });
    return result;
  }

  /**
   * Mark a task as complete.
   * Sets status=300, completedAt=now, completedByUserId=current user.
   * If the task has recurrence, the backend auto-creates the next occurrence.
   */
  static async complete(id: string): Promise<OpResult> {
    const result = await ApiService.executeMutation(VEHICLE_TASK_COMPLETE_MUTATION, {
      where: { id },
    });
    return result;
  }

  /**
   * Remove/delete a vehicle task (soft delete, sets status=10000)
   */
  static async remove(id: string): Promise<OpResult> {
    const result = await ApiService.executeMutation(VEHICLE_TASK_REMOVE_MUTATION, {
      where: { id },
    });
    return result;
  }

  /**
   * Remove/delete multiple vehicle tasks (soft delete)
   */
  static async removeMany(ids: string[]): Promise<OpResult> {
    const where = ids.map((id) => ({ id }));
    const result = await ApiService.executeMutation(VEHICLE_TASK_REMOVE_MANY_MUTATION, {
      where,
    });
    return result;
  }
}